/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useContext, useEffect, useState } from 'react';
import IEnergyManagementProps, {
  IAny,
  TrainingData
} from '../../../DashboardKPIWidgets/IEnergyManagementProps';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../locales/en-US/descriptors';
import useMounted from '../../../shared/useMount/useMount';
import useConnectorAndDynamicProps from '../../../shared/useConnectorAndDynamicProps/useConnectorAndDynamicProps';
import EmaasEnergyUsagesAllUtilityDetailWidget from '../AllUtilitiesDetails/EmaasEnergyUsagesAllUtilityDetailWidget';
import EnergyScopeSavingsByMeterComponent from './EnergyScopeSavingsByMeterWidget/EnergyScopeSavingsByMeterComponent';
import {
  EnergyUsageScopeType,
  EnergyUsageUtilityType
} from '../../../shared/Utils/Constants';
import {
  getEnergyEndDateBasedOnSub,
  getEnergyStartDate
} from '../../../shared/Utils/EnergyUsagesViewDetailsUtils';
import SharedTabsWidget from '../../../shared/SharedTabs/SharedTabs';
import { getIntervalType } from '../../../shared/Utils/PortfolioEnergy/PortfolioEnergyVDUtils';
import { DataContextProvider } from '@forge/data-connector';
import EmaasEnergyUsagesAllUtilitySavingsByMeter from '../AllUtilitiesDetails/EmaasEnergyUsagesAllUtilitySavingByMeter';
import EmaasEUAllUtilityMeterDetails from '../AllUtilitiesDetails/EmaasEUAllUtilityMeterDetails';
import { ApiContext } from '../../../../context/ApiContext';
import {
  EmaasSubscriptionType,
  EnergyUsageDataSourceType
} from '../../../shared/Enums';
import { Icon } from '@scuf/common';
import moment from 'moment';
import EmaasBaselineFormModal from '../AllUtilitiesDetails/EmaasBaselineFormModal';
import _ from 'lodash';
import NoData from '../../../shared/ErrorComponents/NoData';
import EmaasEnergyUsagesAllUtilityDetailWidgetSeparateAPI from '../AllUtilitiesDetails/EmaasEnergyUsagesAllUtilityDetailWidgetSeparateAPI';
import energyScopeSavingsByMeterComponentContentSeparateAPI from './EnergyScopeSavingsByMeterWidget/EnergyScopeSavingsByMeterComponentSeparateAPI';
import EmaasEUAllUtilityMeterDetailsInfinite from '../AllUtilitiesDetails/EmaasEUAllUtilityMeterDetailsInfinite';

const EmassEnergyUsagesElectricityDetials = (props: IEnergyManagementProps) => {
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  const { bearerToken, requestContext, getAllMeterData, emaasContext } = props;
  const [getHeader, setHeader] = useState('');
  const apiContext = useContext(ApiContext);
  const [isOutOfRange, setIsOutOfRange] = useState<boolean>(false);

  const [tableData, setTableData] = useState<IAny>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<{ [key: string]: string }>({});
  const [statusCode, setStatusCode] = useState<number | null>(0);

  const { getEnergyUsageTableData } = apiContext;
  const getTableData = async () => {
    try {
      setLoading(true);
      if (getEnergyUsageTableData) {
        const tableData: any = await getEnergyUsageTableData(t(d.electricity));
        if (tableData && tableData.data) {
          setTableData(tableData.data);
        } else {
          setError({ name: 'No Data Found' });
        }
        setStatusCode(tableData.status);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setStatusCode(500);
    }
  };
  useEffect(() => {
    getTableData();
  }, []);

  const dataSource = requestContext.context.dataSource || '';

  const isSitePremium =
    requestContext.context.subscriptionType ===
      EmaasSubscriptionType.PREMIUM_SUBSCRIPTION &&
    apiContext.toggleSeparateApi &&
    dataSource !== EnergyUsageDataSourceType.UTILITY_METER;

  const startDate = getEnergyStartDate(
    requestContext.context.startTime!,
    requestContext.context.subscriptionType!,
    undefined,
    undefined,
    requestContext.context.dataSource ===
      EnergyUsageDataSourceType.UTILITY_METER
  );
  const endDate = getEnergyEndDateBasedOnSub(
    requestContext.context.subscriptionType!,
    requestContext.context.endTime!,
    requestContext.context.startTime!,
    undefined,
    undefined,
    requestContext.context.dataSource ===
      EnergyUsageDataSourceType.UTILITY_METER
  );

  const isDateRangeValid = () => {
    const sDate = moment(
      getEnergyStartDate(
        requestContext.context.startTime!,
        requestContext.context.subscriptionType!,
        undefined,
        undefined,
        requestContext.context.dataSource ===
          EnergyUsageDataSourceType.UTILITY_METER
      ),
      'YYYY-MM-DD'
    );
    const eDate = moment(
      getEnergyEndDateBasedOnSub(
        requestContext.context.subscriptionType!,
        requestContext.context.endTime!,
        requestContext.context.startTime!,
        undefined,
        undefined,
        requestContext.context.dataSource ===
          EnergyUsageDataSourceType.UTILITY_METER
      ),
      'YYYY-MM-DD'
    );
    return eDate.diff(sDate, 'days') <= 365;
  };

  const { pointHistoryUploadToggle } = useContext(ApiContext);
  const [openmodal, setOpenmodal] = useState(false);

  const connectorConfigPerformanceAggregation = {
    name: 'emaas-energy-usage-performance-aggregation',
    options: {
      startDate: startDate,
      endDate: endDate,
      allDetails: 'true',
      utilityType: EnergyUsageScopeType.ELECTRIC,
      intervalType: getIntervalType(startDate, endDate),
      dataSource
    }
  };
  const connectorConfig = {
    name: isSitePremium ? 'emaas-energy-usage-summary' : 'emaas-energy-usage',
    options: {
      startDate: startDate,
      endDate: endDate,
      allDetails: 'true',
      utilityType: EnergyUsageScopeType.ELECTRIC,
      intervalType: getIntervalType(startDate, endDate),
      dataSource
    }
  };

  const isMounted = useMounted();

  const [dataConnector] = useConnectorAndDynamicProps({
    connectorConfig,
    requestContext,
    bearerToken
  });

  let dataConnectorPerformanceAggregation: any;
  // if (isSitePremium) {
  dataConnectorPerformanceAggregation = useConnectorAndDynamicProps(
    {
      connectorConfig: connectorConfigPerformanceAggregation,
      requestContext,
      bearerToken
    },
    isSitePremium
  )[0];
  // }

  useEffect(() => {
    if (isMounted) {
      if (isDateRangeValid()) {
        setIsOutOfRange(false);
        dataConnector &&
          dataConnector.actions &&
          dataConnector.actions.getAll();
        if (isSitePremium) {
          dataConnectorPerformanceAggregation &&
            dataConnectorPerformanceAggregation.actions &&
            dataConnectorPerformanceAggregation.actions.getAll();

          dataConnectorBreakdownByZone &&
            dataConnectorBreakdownByZone.actions &&
            dataConnectorBreakdownByZone.actions.getAll();
        }
      } else {
        setIsOutOfRange(true);
      }
    }
  }, [requestContext]);

  const connectorConfigBreakdownByZone = {
    name: 'emaas-energy-usage-breakdown-by-zone',
    options: {
      startDate: startDate,
      endDate: endDate,
      allDetails: 'true',
      utilityType: EnergyUsageScopeType.ELECTRIC,
      intervalType: getIntervalType(startDate, endDate),
      groupType: 'Area',
      dataSource
    }
  };

  let dataConnectorBreakdownByZone: IAny;

  // if (isSitePremium) {
  dataConnectorBreakdownByZone = useConnectorAndDynamicProps(
    {
      connectorConfig: connectorConfigBreakdownByZone,
      requestContext,
      bearerToken
    },
    isSitePremium
  )[0];
  // }

  const UtilityBillType = EnergyUsageUtilityType.Electricity;
  let meterTab: {
    className: string;
    title: string;
    component: any;
  }[];

  if (
    requestContext.context.subscriptionType! ===
      EmaasSubscriptionType.PREMIUM_SUBSCRIPTION &&
    emaasContext?.meterKPICEMSToggle &&
    dataSource !== EnergyUsageDataSourceType.UTILITY_METER
  ) {
    meterTab = [
      {
        className: 'emaas-utility-view-details-scope-one',
        title: t(d.metersHeader),
        component: apiContext.enableInfiniteScrollSubmeterKpi
          ? EmaasEUAllUtilityMeterDetailsInfinite
          : EmaasEUAllUtilityMeterDetails
      }
    ];
  } else {
    meterTab = [];
  }
  const displayTabs = {
    displayTabs: [
      {
        className: 'emaas-carbondetail-view-details-total-emissions',
        title: getHeader,
        component: EnergyScopeSavingsByMeterComponent
      },
      ...meterTab
    ],
    bearerToken: bearerToken,
    requestContext: requestContext,
    ActiveIndex: apiContext?.ActiveIndex,
    dataConnector: isSitePremium ? dataConnectorBreakdownByZone : dataConnector,
    subscriptionType: requestContext.context.subscriptionType!,
    setHeader: setHeader,
    UtilityBillType: UtilityBillType,
    getAllMeterData: getAllMeterData,
    setOpenmodal: setOpenmodal,
    baseLineIconCheck: true,
    tableData: tableData,
    statusCode: statusCode,
    loading: loading,
    error: error,
    onRefresh: getTableData,
    utilityType: EnergyUsageScopeType.ELECTRIC,
    toggleSeparateApi: apiContext.toggleSeparateApi,
    dataSourceType: dataSource
  };

  useEffect(() => {
    if (!isDateRangeValid()) {
      setIsOutOfRange(true);
    }
  }, []);

  const [forceUpdate, setForceUpdate] = useState<boolean>(false);
  useEffect(() => {
    const reRender = async () =>
      new Promise<void>((res) => {
        setTimeout(() => {
          setForceUpdate(false);
          res();
        }, 300);
      });
    if (dataSource === EnergyUsageDataSourceType.UTILITY_METER) {
      setForceUpdate(true);
    }
    reRender();
  }, [dataSource]);

  return isOutOfRange ? (
    <div className="noDataDisplay common-error-container">
      <NoData message={t(d.UTILITYYEAROUTOFRANGE)} />
    </div>
  ) : (
    <div className="all-utilities-details">
      {isSitePremium ? (
        <EmaasEnergyUsagesAllUtilityDetailWidgetSeparateAPI
          dataConnector={dataConnector}
          dataConnectorPerformanceAggregation={
            dataConnectorPerformanceAggregation
          }
          headerTitle={t(d.ELECTRICITYPERFORMANCE)}
          requestContext={requestContext}
        />
      ) : (
        <EmaasEnergyUsagesAllUtilityDetailWidget
          dataConnector={dataConnector}
          headerTitle={t(d.ELECTRICITYPERFORMANCE)}
          requestContext={requestContext}
        />
      )}
      <DataContextProvider config={props.connectorConfig}>
        {pointHistoryUploadToggle && openmodal == true && (
          <EmaasBaselineFormModal
            openmodal={openmodal}
            setOpenmodal={setOpenmodal}
            bearerToken={bearerToken}
            requestContext={requestContext}
          />
        )}
        {!forceUpdate && (
          <div>
            <SharedTabsWidget {...displayTabs} />
          </div>
        )}
      </DataContextProvider>
      {/* <EnergyScopeSavingsByMeterComponent
        dataConnector={dataConnector}
        UtilityBillType={UtilityBillType}
        subscriptionType={requestContext.context.subscriptionType!}
      /> */}
    </div>
  );
};
export default EmassEnergyUsagesElectricityDetials;
